<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <!-- SEARCH PANEL -->
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                Search Form
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="submit">
                  <v-row class="pa-2">
                    <!-- MONTH PICKER -->
                    <v-col lg="4" md="4" cols="12">
                      <v-date-input
                        type="month"
                        v-model="reconciliation_month.value.value"
                        label="Month"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar-month"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        clearable
                        :error-messages="reconciliation_month.errorMessage.value"
                      />
                    </v-col>

                    <!-- ACCOUNT HEAD -->
                    <v-col lg="4" md="4" cols="12">
                      <v-autocomplete
                        v-model="payment_acc_account_head_id.value.value"
                        :items="getPaymentAccountHeadList"
                        :item-title="(item) => `${item.name ?? 'Select'}`"
                        item-value="id"
                        label="Payment Account Head"
                        variant="outlined"
                        density="compact"
                        prepend-inner-icon="mdi-bank"
                        clearable
                      
                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
/>
                    </v-col>

                    <!-- SEARCH BUTTON -->
                    <v-col lg="2" md="2" cols="12">
                      <v-btn class="bg-brand w-15" type="submit" ref="searchButton">
                        Search
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>

    <!-- BANK RECONCILIATION RECORD -->
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                Bank Reconciliation Statement
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row class="py-2">

                  <v-col md="12" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group variant="outlined" density="compact" size="x-small" divided>
                        <v-btn prepend-icon="mdi-download" variant="flat" color="success" @click="exportToCSV">CSV</v-btn>
                        <v-btn prepend-icon="mdi-download" variant="flat" color="primary" @click="exportToExcel">EXCEL</v-btn>
                        <v-btn prepend-icon="mdi-download" variant="flat" color="red" @click="exportToPDF">PDF</v-btn>
                      </v-btn-group>
                    </div>
                  </v-col>
                </v-row>

                <!-- STATEMENT UI -->
                <div v-if="getSchema" class="pa-4">
                  <div class="text-center mb-4">
                    <h2 class="font-weight-bold">Bank Reconciliation Statement</h2>
                    <div>Date: {{ formattedRange }}</div>
                    <div class="mt-2 font-weight-bold text-h6">{{ getSchema?.name || '' }}</div>
                  </div>

                  <!-- BANK BOOK BALANCE -->
                  <table class="statement-table" width="100%">
                    <tbody> 
                        <tr>
                          <th colspan="5" class="text-left">{{ getSchema?.bank_book_closing_balance?.name || '' }}</th>
                          <th class="text-right">{{ getSchema?.bank_book_closing_balance?.balance || '' }}</th>
                        </tr>
                    </tbody>
                  </table>

                  <!-- UNPAID CHEQUES -->
                  <table class="statement-table" width="100%">
                    <tbody>
                                            <tr>
                      <th colspan="6" class="text-left">{{ getSchema?.unpaid_issued_cheques?.name || '' }}</th>
                    </tr>
                    <tr class="heading">
                      <th>Date</th>
                      <th>Voucher No</th>
                      <th>Payee</th>
                      <th>Cheque No</th>
                      <th>Net Payment</th>
                      <th>TK</th>
                    </tr>
                    <tr v-for="(item, i) in getSchema?.unpaid_issued_cheques?.transactions || []" :key="i">
                      <td>{{ item.voucher_date }}</td>
                      <td>{{ item.code }}</td>
                      <td>{{ item.party?.name }}</td>
                      <td>{{ item.cheque_no }}</td>
                      <td>{{ item.amount }}</td>
                      <td class="text-right">{{ item.amount }}</td>
                    </tr>
                    <tr>
                      <td colspan="5" class="text-right font-weight-bold">Total</td>
                      <td class="text-right font-weight-bold">{{ getSchema?.total_unpaid_issued_amount || 0 }}</td>
                    </tr>
                    </tbody>
                  </table>

                  <!-- UNCLEARED RECEIPTS -->
                  <table class="statement-table" width="100%">
                   <tbody>
                         <tr>
                          <th colspan="6" class="text-left">{{ getSchema?.uncollected_deposited_cheques?.name || '' }}</th>
                        </tr>
                        <tr class="heading">
                          <th>Date</th>
                          <th>Receipt No</th>
                          <th>Depositor</th>
                          <th>Cheque No</th>
                          <th>Net Receipt</th>
                          <th>TK</th>
                        </tr>
                        <tr v-if="(getSchema?.uncollected_deposited_cheques?.transactions || []).length === 0">
                          <td colspan="6" class="text-center grey--text">No records found</td>
                        </tr>
                        <tr v-for="(item, i) in getSchema?.uncollected_deposited_cheques?.transactions || []" :key="i">
                          <td>{{ item.voucher_date }}</td>
                          <td>{{ item.code }}</td>
                          <td>{{ item.party?.name }}</td>
                          <td>{{ item.cheque_no }}</td>
                          <td>{{ item.amount }}</td>
                          <td class="text-right">{{ item.amount }}</td>
                        </tr>
                        <tr>
                          <td colspan="5" class="text-right font-weight-bold">Total</td>
                          <td class="text-right font-weight-bold">{{ getSchema?.total_uncollected_deposited_amount || 0 }}</td>
                        </tr>
                   </tbody>
                  </table>

                  <!-- CLOSING BALANCE -->
                  <table class="statement-table" width="100%">
                    <tbody>
                        <tr>
                        <th colspan="5" class="text-left">{{ getSchema?.closing_balance?.name || '' }}</th>
                        <th class="text-right">{{ getSchema?.closing_balance?.balance || '' }}</th>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { ref } from "vue";
import { toast } from "vue3-toastify";

export default {
  data: () => ({
    panel: [0],
    search: "",
  }),

  computed: {
    getSchema() {
      return this.$store.getters.bankReconciliationStatement;
    },
    getPaymentAccountHeadList() {
      return this.$store.getters.getPaymentAccountHeadList;
    },
    formattedRange() {
      return this.reconciliation_month?.value?.value || "--";
    },
  },

  created() {
    // if (!this.$store.getters.getPaymentAccountHeadList?.length) {
      this.loading = true;
      this.$store
        .dispatch("getPaymentAccountHeadList", { type: "BANK" })
        .finally(() => {
          this.loading = false;
        });
    // }
  },

  setup() {
    const store = useStore();
    const { handleSubmit } = useForm({
      validationSchema: yup.object({}),
    });

    const payment_acc_account_head_id = useField("payment_acc_account_head_id");
    const reconciliation_month = useField("reconciliation_month");
    const loading = ref(false);

    const submit = handleSubmit((values) => {
      loading.value = true;

      values.month = reconciliation_month.value.value; // YYYY-MM
      values.acc_account_head_id = payment_acc_account_head_id.value.value;

      store
        .dispatch("bankReconciliationStatement", values)
        .then(() => (loading.value = false))
        .catch((error) => {
          loading.value = false;
          toast.error(error.response?.data?.message || "Error fetching data");
        });
    });

    return {
      submit,
      loading,
      payment_acc_account_head_id,
      reconciliation_month,
    };
  },

  mounted() {
    this.$nextTick(() => {
      this.$refs.searchButton?.$el?.click();
    });
  },
};
</script>

<style scoped>
.statement-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 16px;
  font-size: 14px;
}

.statement-table th,
.statement-table td {
  border: 1px solid #cfcfcf;
  padding: 6px 8px;
}

.statement-table .heading th {
  background: #eaeaea;
  font-weight: bold;
}
</style>
